<script lang="ts">
  import { VITE_API_BASE_URL } from '$lib/env';
  import CameraIcon from '$icons/CameraIcon.svelte';
  import CornerIcon from '$icons/CornerIcon.svelte';
  import EditSquaredIcon from '$icons/edit/EditSquaredIcon.svelte';
  import { toastError } from '$utils/toasts';
  import ImageCropper from './ImageCropper.svelte';
  import imageCompression, { type Options } from 'browser-image-compression';

  import LoaderIcon from '$icons/loaders/LoaderIcon.svelte';

  let avatar: FileList | null = null;
  let croppedAvatar: FileList | null = null;
  let processingAvatar = false;
  export let avatarId = 0;
  export let avatarUrl = '';
  export let isFullSize = false;
  let fileInput: HTMLInputElement;
  $: id = crypto.randomUUID();

  // This will be applied after cropping, resizing, and compression
  const maxSizeMB = 0.35;

  const resetInput = () => {
    if (fileInput) fileInput.value = '';
  };

  const compressImage = async () => {
    const options: Options = {
      maxSizeMB,
      maxWidthOrHeight: 400,
      useWebWorker: true,
      maxIteration: 6,
    };
    try {
      let file: File;
      processingAvatar = true;
      // biome-ignore lint/style/noNonNullAssertion: TODO: Remove this non-null assertion
      if (croppedAvatar![0].size / 1024 / 1024 <= maxSizeMB) {
        // biome-ignore lint/style/noNonNullAssertion: TODO: Remove this non-null assertion
        file = croppedAvatar![0];
      } else {
        // biome-ignore lint/style/noNonNullAssertion: TODO: Remove this non-null assertion
        file = await imageCompression(croppedAvatar![0], options);
        console.log(`compressedFile size ${file.size / 1024 / 1024} MB`); // smaller than maxSizeMB
      }
      if (file.size / 1024 / 1024 > maxSizeMB) {
        toastError(
          'Could not compress your image to the required size. Please try a smaller image.',
        );
        croppedAvatar = null;
        avatar = null;
      } else {
        await handleAvatarUpload(file);
      }
    } catch (error) {
      processingAvatar = false;
      toastError(error);
    }
  };
  const handleAvatarUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);
    const xhr = new XMLHttpRequest();
    xhr.onload = async () => {
      if (xhr.status !== 200) {
        if (xhr.response === '"File too large"') {
          toastError('Maximum image size after compression is 350KB');
        } else {
          toastError(xhr.response);
        }
        croppedAvatar = null;
        avatar = null;
        avatarUrl = '';
      } else {
        avatarId = JSON.parse(xhr.response).id;
        avatarUrl = JSON.parse(xhr.response).url;
        // await invalidateAll();
      }
      processingAvatar = false;
    };
    xhr.onerror = () => {
      console.log('Failed to upload avatar');
      toastError('Failed to upload avatar');
    };
    xhr.open('POST', `${VITE_API_BASE_URL}/api/users/avatar`, true);
    xhr.send(formData);
  };
  let selectedAvatarDimensions: { width?: number; height?: number } = {};
  let aspectSquare = false;
  const handleSelectedFile = async () => {
    if (avatar?.[0]) {
      croppedAvatar = null;
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();

        img.onload = () => {
          selectedAvatarDimensions.width = img.naturalWidth;
          selectedAvatarDimensions.height = img.naturalHeight;
          aspectSquare = selectedAvatarDimensions?.width / selectedAvatarDimensions?.height === 1;
        };

        const result = e.target?.result;
        if (typeof result === 'string') {
          img.src = result;
        }
      };

      reader.readAsDataURL(avatar?.[0]);
    }
  };

  export let showImageEditing = false;
  $: showImageEditing =
    // biome-ignore lint/complexity/noUselessTernary: TODO: Fix this
    !aspectSquare && !croppedAvatar && avatar ? true : false;
  $: if (avatar && aspectSquare) {
    croppedAvatar = avatar;
  }
  $: croppedAvatar && compressImage();
</script>

{#if showImageEditing}
  <ImageCropper
    {isFullSize}
    bind:showImageEditing
    bind:originalImage={avatar}
    bind:croppedImage={croppedAvatar}
  />
{/if}

<div class="rounded-3xs relative h-[88px] w-[88px] overflow-hidden">
  <label
    for={id}
    class:avatar
    class="rounded-3xs border-gray-dark-4 relative flex h-full w-full cursor-pointer items-center justify-center overflow-hidden border border-dashed"
  >
    <div class="corner-square absolute inset-0">
      <CornerIcon class="text-gray-dark-12 absolute right-0 top-0" />
      <CornerIcon class="text-gray-dark-12 absolute bottom-0 left-0 rotate-180" />
    </div>
    <div
      class:hidden={!processingAvatar}
      class="absolute inset-0 z-10 flex items-center justify-center"
    >
      <LoaderIcon class="text-mint-dark-8" />
    </div>
    <input
      {id}
      bind:this={fileInput}
      type="file"
      class="sr-only"
      accept="image/png, image/jpeg, image/jpg, image/webp"
      bind:files={avatar}
      on:change={handleSelectedFile}
      on:click={resetInput}
    />
    {#if croppedAvatar || avatarUrl}
      <div
        class="icon icon-edit-squared bottom-2xs right-2xs bg-gray-dark-13 p-2xs absolute z-10 rounded"
      >
        <EditSquaredIcon class="text-gray-dark-1 aspect-square w-4" />
      </div>
    {:else}
      <div class="icon icon-camera p-2xs rounded">
        <CameraIcon class="text-gray-dark-12 aspect-square w-4" />
      </div>
    {/if}
    {#if avatarUrl && !croppedAvatar}
      <img src={avatarUrl} alt="user-avatar" class="absolute inset-0 aspect-square w-full" />
    {:else if croppedAvatar}
      <img
        src={URL.createObjectURL(croppedAvatar?.[0])}
        alt="user-avatar"
        class="absolute inset-0"
      />
    {/if}
  </label>
</div>

<style lang="postcss">
  label:hover > .icon-camera,
  label:focus > .icon-camera {
    @apply bg-gray-dark-5;
  }
  label:hover > .icon-edit-squared,
  label:focus > .icon-edit-squared {
    @apply bg-gray-dark-12;
  }
  label:focus > .icon {
    @apply border-mint-dark-8 ring-mint-dark-4 border ring-[3px];
  }
  .avatar {
    @apply border-none;
  }
  .corner-square {
    @apply block;
  }
  .avatar > .corner-square {
    @apply hidden;
  }
</style>
